import type { HighlightGeometry } from '../types';
import { CommentModal } from './CommentModal';
import { LinkNoteScreen } from './LinkNoteScreen';
import { PDFNoteCode } from '../../../../../pages/notes/models/PDFNoteCode';

interface PendingAnnotationModalsProps {
    editingHighlight: { id: string; note: string } | null;
    editingComment: string;
    onEditingCommentChange: (value: string) => void;
    onCommitEdit: () => void;
    onCancelEdit: () => void;
    onDelete: (id: string) => void;
    editingIsCritical?: boolean;
    onEditingIsCriticalChange?: (val: boolean) => void;
    editingCodeId?: number;
    onEditingCodeIdChange?: (val: number | undefined) => void;

    showLinkScreen: boolean;
    linkedNote: { id: number; title: string } | null;
    onLinkedNoteChange: (note: { id: number; title: string } | null) => void;
    onShowLinkScreenChange: (show: boolean) => void;
    workflowInstanceId?: number;
    noteObjectId?: number;
    workflowInstanceAttachmentId?: number;
    onLinkToExisting?: (geometry: HighlightGeometry, noteId: number) => Promise<void>;

    pendingAnnotation: {
        title: string;
        quote?: string;
        geometry: HighlightGeometry;
        commit: () => void;
    } | null;
    pendingComment: string;
    onPendingCommentChange: (value: string) => void;
    onCancelPending: () => void;
    pendingIsCritical?: boolean;
    onPendingIsCriticalChange?: (val: boolean) => void;
    pendingCodeId?: number;
    onPendingCodeIdChange?: (val: number | undefined) => void;

    codes?: PDFNoteCode[];
    isExecutive?: boolean;
    showCriticalCode?: boolean;
}

export function PendingAnnotationModals({
    editingHighlight,
    editingComment,
    onEditingCommentChange,
    onCommitEdit,
    onCancelEdit,
    onDelete,
    editingIsCritical,
    onEditingIsCriticalChange,
    editingCodeId,
    onEditingCodeIdChange,
    showLinkScreen,
    linkedNote,
    onLinkedNoteChange,
    onShowLinkScreenChange,
    workflowInstanceId,
    noteObjectId,
    workflowInstanceAttachmentId,
    onLinkToExisting,
    pendingAnnotation,
    pendingComment,
    onPendingCommentChange,
    onCancelPending,
    pendingIsCritical,
    onPendingIsCriticalChange,
    pendingCodeId,
    onPendingCodeIdChange,
    codes,
    isExecutive,
    showCriticalCode,
}: PendingAnnotationModalsProps) {
    return (
        <>
            {!showLinkScreen && editingHighlight && (
                <CommentModal
                    title="Замечание к метке"
                    comment={editingComment}
                    onCommentChange={onEditingCommentChange}
                    onSave={onCommitEdit}
                    onCancel={onCancelEdit}
                    onDelete={() => { onDelete(editingHighlight.id); onCancelEdit(); }}
                    submitLabel="Сохранить"
                    submitIcon="fa-solid fa-floppy-disk"
                    isCritical={editingIsCritical}
                    onIsCriticalChange={onEditingIsCriticalChange}
                    codeId={editingCodeId}
                    onCodeIdChange={onEditingCodeIdChange}
                    codes={codes}
                    isExecutive={isExecutive}
                    showCriticalCode={showCriticalCode}
                />
            )}

            {showLinkScreen && workflowInstanceId != null && noteObjectId != null && (
                <LinkNoteScreen
                    workflowInstanceId={workflowInstanceId}
                    noteObjectId={noteObjectId}
                    workflowInstanceAttachmentId={workflowInstanceAttachmentId ?? 0}
                    onSelect={(note) => {
                        onLinkedNoteChange({ id: note.Id!, title: note.Title ?? '' });
                        onShowLinkScreenChange(false);
                    }}
                    onBack={() => onShowLinkScreenChange(false)}
                />
            )}

            {pendingAnnotation && (
                <CommentModal
                    title={pendingAnnotation.title}
                    quote={pendingAnnotation.quote}
                    comment={pendingComment}
                    onCommentChange={onPendingCommentChange}
                    onSave={pendingAnnotation.commit}
                    onCancel={onCancelPending}
                    submitLabel="Отправить"
                    onLinkToExisting={onLinkToExisting ? () => onShowLinkScreenChange(true) : undefined}
                    linkedNote={linkedNote}
                    onResetLink={() => onLinkedNoteChange(null)}
                    onSaveLinked={async () => {
                        if (!linkedNote) return;
                        await onLinkToExisting!(pendingAnnotation.geometry, linkedNote.id);
                        onCancelPending();
                    }}
                    isCritical={pendingIsCritical}
                    onIsCriticalChange={onPendingIsCriticalChange}
                    codeId={pendingCodeId}
                    onCodeIdChange={onPendingCodeIdChange}
                    codes={codes}
                    isExecutive={isExecutive}
                    showCriticalCode={showCriticalCode}
                />
            )}
        </>
    );
}
